import { useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import CloseIcon from '@mui/icons-material/Close';
import { DndContext, closestCenter } from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  horizontalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GenericDialog } from '@/components/shared/GenericDialog';
import { containedButtonStyle } from '@/theme';
import { abyssColors } from '@/theme/colors';

export type DraggableFieldKeyItem = {
  key: string;
  label: string;
  unknown?: boolean;
};

type DraggableFieldKeyChipsProps = Readonly<{
  items: DraggableFieldKeyItem[];
  disabled?: boolean;
  onReorder: (keys: string[]) => void;
  onRemove: (key: string) => void;
}>;

function SortableFieldChip({
  item,
  disabled,
  onRemove,
}: Readonly<{
  item: DraggableFieldKeyItem;
  disabled: boolean;
  onRemove: (key: string) => void;
}>) {
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: item.key,
    disabled,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const chipSx = {
    ...(item.unknown === true
      ? {}
      : {
          color: abyssColors.neutral[100],
          backgroundColor: abyssColors.primary[400],
        }),
    '& .MuiChip-deleteIcon': {
      color: abyssColors.neutral[100],
      '&:hover': {
        color: abyssColors.neutral[200],
      },
    },
  };

  return (
    <>
      <Chip
        ref={setNodeRef}
        style={style}
        label={
          <Box sx={{ cursor: disabled ? 'default' : 'grab' }} {...attributes} {...listeners}>
            {item.label}
          </Box>
        }
        size="small"
        color={item.unknown === true ? 'warning' : 'default'}
        variant={item.unknown === true ? 'outlined' : 'filled'}
        deleteIcon={
          disabled ? undefined : (
            <CloseIcon
              sx={{
                color: abyssColors.neutral[100],
                '&:hover': {
                  color: abyssColors.neutral[200],
                },
              }}
            />
          )
        }
        onDelete={
          disabled
            ? undefined
            : (event) => {
                event.stopPropagation();
                setShowDeleteDialog(true);
              }
        }
        sx={chipSx}
      />
      {showDeleteDialog && (
        <GenericDialog
          open={showDeleteDialog}
          onClose={() => setShowDeleteDialog(false)}
          title="Remove field"
          content={
            <Typography fontWeight={400} fontSize="14px" color={abyssColors.primary[500]}>
              Are you sure you want to remove this field from the list?
            </Typography>
          }
          actions={
            <>
              <Button
                variant="text"
                onClick={() => setShowDeleteDialog(false)}
                sx={{ color: abyssColors.primary[500] }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={() => {
                  onRemove(item.key);
                  setShowDeleteDialog(false);
                }}
                sx={containedButtonStyle}
              >
                Remove
              </Button>
            </>
          }
          actionsStyles={{ mr: 3, pb: 3 }}
        />
      )}
    </>
  );
}

export function DraggableFieldKeyChips(props: Readonly<DraggableFieldKeyChipsProps>) {
  const { items, disabled = false, onReorder, onRemove } = props;
  const keys = items.map((item) => item.key);

  if (items.length === 0) {
    return null;
  }

  return (
    <Box sx={{ width: '100%', minWidth: 0 }}>
      <DndContext
        collisionDetection={closestCenter}
        onDragEnd={(event) => {
          const { active, over } = event;
          if (!over || active.id === over.id) {
            return;
          }

          const oldIndex = keys.indexOf(active.id as string);
          const newIndex = keys.indexOf(over.id as string);

          if (oldIndex === -1 || newIndex === -1) {
            return;
          }

          onReorder(arrayMove(keys, oldIndex, newIndex));
        }}
      >
        <SortableContext items={keys} strategy={horizontalListSortingStrategy}>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {items.map((item) => (
              <SortableFieldChip
                key={item.key}
                item={item}
                disabled={disabled}
                onRemove={onRemove}
              />
            ))}
          </Stack>
        </SortableContext>
      </DndContext>
    </Box>
  );
}
